
import { useState, useEffect } from 'react';

interface ScheduleChange {
  id: string;
  schedule_id: string | null;
  action_type: 'INSERT' | 'UPDATE' | 'DELETE';
  action_by: string | null;
  action_timestamp: string;
  field_changed: string | null;
  old_value: string | null;
  new_value: string | null;
  shift_title: string | null;
  shift_description: string | null;
  shift_start_date: string | null;
  shift_end_date: string | null;
  shift_location: string | null;
  team_name: string | null;
  assigned_guard_names: string[] | null;
  change_reason: string | null;
  duration_hours: number | null;
  action_by_name: string | null;
}

const STORAGE_KEY = 'schedule_logs_cache';
const LAST_SYNC_KEY = 'schedule_logs_last_sync';

export const useScheduleLogsStorage = () => {
  const [cachedLogs, setCachedLogs] = useState<ScheduleChange[]>([]);
  const [lastSync, setLastSync] = useState<string | null>(null);

  // Load cached data on mount
  useEffect(() => {
    const cached = localStorage.getItem(STORAGE_KEY);
    const lastSyncTime = localStorage.getItem(LAST_SYNC_KEY);
    
    if (cached) {
      try {
        setCachedLogs(JSON.parse(cached));
      } catch (error) {
        console.error('Error parsing cached logs:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    
    if (lastSyncTime) {
      setLastSync(lastSyncTime);
    }
  }, []);

  const updateCache = (logs: ScheduleChange[]) => {
    const sortedLogs = logs.sort((a, b) => 
      new Date(b.action_timestamp).getTime() - new Date(a.action_timestamp).getTime()
    );
    
    setCachedLogs(sortedLogs);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sortedLogs));
    
    const now = new Date().toISOString();
    setLastSync(now);
    localStorage.setItem(LAST_SYNC_KEY, now);
  };

  const mergeWithCache = (newLogs: ScheduleChange[]) => {
    // Create a map of existing logs by ID for faster lookup
    const existingLogsMap = new Map(cachedLogs.map(log => [log.id, log]));
    
    // Add new logs that don't exist in cache
    const mergedLogs = [...cachedLogs];
    let hasNewData = false;
    
    newLogs.forEach(log => {
      if (!existingLogsMap.has(log.id)) {
        mergedLogs.push(log);
        hasNewData = true;
      }
    });
    
    if (hasNewData) {
      updateCache(mergedLogs);
    }
    
    return mergedLogs;
  };

  const clearCache = () => {
    setCachedLogs([]);
    setLastSync(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LAST_SYNC_KEY);
  };

  return {
    cachedLogs,
    lastSync,
    updateCache,
    mergeWithCache,
    clearCache
  };
};
